/**
 * resolveNavLink — turn a stored nav item into the final href to render.
 *
 * EPIC-13 · FEAT-13.4 · STORY-13.4.05.
 *
 * The schema stores each nav item as `{label, type, target, open_new_tab, icon?}`
 * (see `siteSettingsNav.mergeNavItem`). The storefront's Header / Footer
 * components need a string href to put on `<a>` / `<Link>`. This pure
 * helper does that translation, plus a couple of integration steps:
 *
 *   - page    → `/p/{slug}` BY DEFAULT, but redirected via the
 *               `pageRedirects` map (built from `page_redirects/{old}.target`)
 *               so an old-slug nav item silently flows to the new URL.
 *               Special case: `home` always resolves to `/` (no Pages-CMS
 *               doc; matches the existing storefront route).
 *   - section → `/#section-{id}` (matches the homepage anchors).
 *   - url     → the target as-is (already validated at write-time by
 *               `isLikelySafeUrl`, so we trust it here).
 *   - action  → returns a descriptor `{ kind: 'action', name }` rather
 *               than a string. Consumers branch on the return shape and
 *               wire `onClick` to the matching handler. The href falls
 *               back to `'#'` so a `<a>` still degrades gracefully.
 *
 * Returns either a string (for navigable items) OR an action descriptor.
 * Consumers should normalise via `hrefOf(resolved)` below — see the
 * Header / Footer storefront integration that ships in this PR.
 */

const STOREFRONT_HOME_ROUTE = '/';
const STOREFRONT_PAGE_ROUTE_PREFIX = '/p/';
const STOREFRONT_SECTION_ANCHOR_PREFIX = '/#section-';

/**
 * Special-case slugs whose storefront URL is NOT under `/p/`. These
 * pages predate the Pages CMS or have their own routes
 * (Home / About / Contact / Products listing). The map lets a
 * page-type nav item point at, say, `about` and resolve to `/about`
 * — the route the storefront actually has — rather than `/p/about`
 * which doesn't exist yet.
 *
 * Keep this list lean; consolidating into the Pages CMS over time is
 * the goal.
 */
const LEGACY_PAGE_ROUTES = Object.freeze({
  home:     STOREFRONT_HOME_ROUTE,
  products: '/products',
  about:    '/about',
  contact:  '/contact',
});

/**
 * @param {Object} item — nav item from `siteSettingsNav.mergeNavItem`.
 * @param {Object} [opts]
 * @param {Map<string,string>|Object<string,string>} [opts.pageRedirects]
 *   Map of `oldSlug → newSlug` (from `/page_redirects/{slug}.target`).
 *   When a page-type nav item's target matches an entry, the resolver
 *   follows the redirect ONCE (no chain-following — same contract as
 *   `pagesService.resolvePageOrRedirect`).
 * @returns {string | { kind: 'action', name: string, href: string }}
 */
export function resolveNavLink(item, opts = {}) {
  if (!item || typeof item !== 'object') return '#';
  const type = item.type;
  const rawTarget = typeof item.target === 'string' ? item.target : '';

  switch (type) {
    case 'page': {
      const slug = applyRedirect(rawTarget, opts.pageRedirects);
      if (!slug) return '#';
      if (LEGACY_PAGE_ROUTES[slug]) return LEGACY_PAGE_ROUTES[slug];
      return `${STOREFRONT_PAGE_ROUTE_PREFIX}${slug}`;
    }

    case 'section': {
      if (!rawTarget) return '#';
      return `${STOREFRONT_SECTION_ANCHOR_PREFIX}${rawTarget}`;
    }

    case 'url': {
      // URL items are validated at write-time via isLikelySafeUrl —
      // we don't re-validate at render. An empty string falls back
      // to `#` so an empty href doesn't trigger a same-page reload.
      return rawTarget || '#';
    }

    case 'action': {
      const name = rawTarget || '';
      return { kind: 'action', name, href: '#' };
    }

    default:
      // Unknown type → safe fallback. mergeNavItem already coerces
      // unknown types to 'url', so this branch is defensive.
      return '#';
  }
}

function applyRedirect(slug, redirects) {
  if (!slug) return slug;
  if (!redirects) return slug;
  if (typeof redirects.get === 'function') {
    const next = redirects.get(slug);
    return typeof next === 'string' && next.length > 0 ? next : slug;
  }
  if (typeof redirects === 'object' && Object.prototype.hasOwnProperty.call(redirects, slug)) {
    const next = redirects[slug];
    return typeof next === 'string' && next.length > 0 ? next : slug;
  }
  return slug;
}

/**
 * Normalise the return of `resolveNavLink` to a plain href string.
 * Action descriptors are flattened to their `.href` (default `'#'`)
 * so `<a href={hrefOf(resolved)}>` always gets a string.
 */
export function hrefOf(resolved) {
  if (typeof resolved === 'string') return resolved;
  if (resolved && typeof resolved === 'object' && typeof resolved.href === 'string') {
    return resolved.href;
  }
  return '#';
}

/**
 * Helper for consumers wiring action onClick. Returns the action name
 * if the resolved value is an action descriptor; null otherwise.
 */
export function actionOf(resolved) {
  if (resolved && typeof resolved === 'object' && resolved.kind === 'action') {
    return resolved.name;
  }
  return null;
}

export const RESOLVE_NAV_LINK_CONSTANTS = Object.freeze({
  homeRoute: STOREFRONT_HOME_ROUTE,
  pageRoutePrefix: STOREFRONT_PAGE_ROUTE_PREFIX,
  sectionAnchorPrefix: STOREFRONT_SECTION_ANCHOR_PREFIX,
  legacyPageRoutes: LEGACY_PAGE_ROUTES,
});
